"use client";

import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import BottomCurves from "../curves/bottomCurves";

gsap.registerPlugin(ScrollTrigger);

export default function CaseStudiesCardStack({ data }: { data: any }) {
    const caseStudiesRef = useRef<HTMLDivElement | null>(null);
    const cardDescriptionRef = useRef<HTMLDivElement | null>(null);

    const [isVisible, setIsVisible] = useState(false); // Visibility state

    const initializeGsapAnimations = () => {
        let cards: any = gsap.utils.toArray(".case");

        let stickDistance = 0;

        let firstCardST = ScrollTrigger.create({
            trigger: cards[0],
            start: "top 25%",
        });

        let lastCardST = ScrollTrigger.create({
            trigger: cards[cards.length - 1],
            start: "center center",
        });

        // Scale-down animation for cards
        cards.forEach((card: any, index: number) => {
            const scale = 1 - (cards.length - index) * 0.025;

            gsap.to(card, {
                scale: scale,
                scrollTrigger: {
                    trigger: card,
                    start: `top 25%`,
                    end: () => lastCardST.start + stickDistance,
                    pin: true,
                    pinSpacing: false,
                    toggleActions: "restart none none reverse",
                    markers: false,
                },
            });
        });

        // Pin the description
        gsap.timeline({
            scrollTrigger: {
                trigger: caseStudiesRef.current,
                start: () => firstCardST.start,
                end: () => lastCardST.start + stickDistance,
                scrub: true,
                pin: cardDescriptionRef.current,
                pinSpacing: false,
                markers: false,
            },
        });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setIsVisible(true); // Section is visible, trigger GSAP
                }
            },
            {
                root: null, // Observe relative to the viewport
                threshold: 0, // Trigger when 10% of the section is visible
            }
        );

        if (caseStudiesRef.current) {
            observer.observe(caseStudiesRef.current);
        }

        return () => {
            if (caseStudiesRef.current) {
                observer.unobserve(caseStudiesRef.current);
            }
        };
    }, []);

    // Initialize GSAP animations when the section becomes visible
    useEffect(() => {
        if (isVisible && window.innerWidth > 767) {
            initializeGsapAnimations();
        }
    }, [isVisible]);

    if (data.__component === "blocks.case-studies") {
        return (
            <section
                // className="bg-rainbow py-[7.292vw] flex flex-col items-center"
                className="bg-rainbow pt-12 md:pt-20 pb-[7.292vw]"
                ref={caseStudiesRef}
            >
                <div className="pb-4 md:pb-10" ref={cardDescriptionRef}>
                    <h3 className="text-font-60 text-center">{data.heading}</h3>
                </div>

                <div className="cardsHolder overflow-hidden">
                    {data.cases.map((project: any, index: number) => (
                        <div
                            key={`card-${index}`}
                            className={`relative case hover:cursor-pointer rounded-xl md:rounded-[50px] overflow-clip ${
                                index !== data.cases.length - 1
                                    ? "mb-[5vw] md:mb-[10vw]"
                                    : "mb-[20vw] md:mb-[10vw]"
                            }`}
                        >
                            <iframe
                                className="w-full aspect-[4/3] md:aspect-[1415/595] h-full"
                                width="100%"
                                height="100%"
                                src={project.redirectLink}
                                title={project.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                // allowFullScreen="false"
                                // @ts-ignore
                                donotallowfullscreen="true"
                                frameBorder={0}
                            ></iframe>
                        </div>
                    ))}
                </div>
                <BottomCurves color="light grey" direction={"left"} />
            </section>
        );
    }
    return null;
}
